/**
 * 관계 재연결(담당강사 배정·변경) 응답에 실리는 수강권 복구 결과.
 *
 * 재연결은 그 회원의 종료된 수강권을 되살리는데, 서버가 이것을 **문서 단위로** 처리한다.
 * 환불이 진행 중이거나 인계된 수강권은 되살리지 않고 건너뛴다(G8). 예전에는 반환값이
 * 버려져서 화면이 언제나 "전부 복구됐다"고 보였다.
 *
 * 재시도 버튼은 두지 않는다. 같은 재연결을 다시 눌러도 관계가 이미 활성이면 서버가
 * 복구 단계를 타지 않아 아무 일도 일어나지 않는다. 사용자가 취할 수 있는 행동은
 * "잠시 후 다시 시도" 또는 "고객센터 문의" 둘뿐이므로 문구로만 안내한다.
 *
 * 계약 원본: rappo `src/utils/membershipRecoveryResponse.ts` (커밋 cdafc821)
 */

export type MembershipRecoveryResult = {
    recoveredCount: number;
    skippedMembershipIds: string[];
    skipped: { membershipId: string; reason: string }[];
};

/**
 * 응답 본문에서 건너뛴 수강권 수를 읽는다.
 *
 * 구 백엔드 응답에는 `membershipRecovery` 키가 없다 → 0. 화면은 기존과 동일하게 동작한다.
 */
export const getSkippedMembershipCount = (data?: unknown): number => {
    if (!data || typeof data !== 'object') return 0;
    const recovery = (data as Record<string, unknown>).membershipRecovery;
    if (!recovery || typeof recovery !== 'object') return 0;
    const skipped = (recovery as Record<string, unknown>).skippedMembershipIds;
    return Array.isArray(skipped) ? skipped.length : 0;
};

/** 일괄 담당강사 변경 응답(`results[]`)의 건너뛴 수강권 합계. */
export const getBulkSkippedMembershipCount = (data?: unknown): number => {
    if (!data || typeof data !== 'object') return 0;
    const results = (data as Record<string, unknown>).results;
    if (!Array.isArray(results)) return 0;
    return results.reduce<number>((sum, entry) => sum + getSkippedMembershipCount(entry), 0);
};

/**
 * 안내에 덧붙일 문구. 건너뛴 것이 없으면 빈 문자열이라 기존 성공 문구가 그대로 남는다.
 *
 * 몇 건인지 밝힌다 — "일부"만 말하면 관리자가 무엇을 확인해야 할지 알 수 없다.
 */
export const getMembershipRecoveryNotice = (skippedCount: number): string => {
    if (skippedCount <= 0) return '';
    return `\n\n일부 수강권이 복구되지 않았습니다(${skippedCount}건).\n잠시 후 재연결을 다시 시도하거나 고객센터로 문의해 주세요.`;
};

/** 성공 안내 문구 뒤에 복구 실패 안내를 붙인다. */
export const appendMembershipRecoveryNotice = (message: string, data?: unknown): string =>
    `${message}${getMembershipRecoveryNotice(getSkippedMembershipCount(data))}`;
